<template>
  <v-card flat>
    <!-- Скелетон для загрузки -->
    <template v-if="isLoading">
      <v-card-text class="form-wrapper">
        <div v-for="i in 4" :key="i" class="form-row">
          <div class="form-label">
            <v-skeleton-loader type="text" width="100" />
          </div>
          <v-skeleton-loader type="text" width="100%" />
        </div>
        <div class="form-row">
          <v-skeleton-loader type="card" width="100%" />
        </div>
      </v-card-text>
    </template>

    <!-- Ошибка -->
    <template v-else-if="hasError">
      <v-card-text>
        <v-alert
          type="error"
          variant="tonal"
          closable
          @click:close="hasError = false"
        >
          {{ errorMessage || "Ошибка загрузки данных" }}
        </v-alert>
      </v-card-text>
    </template>

    <!-- Основной контент -->
    <template v-else>
      <v-card-text class="form-wrapper">
        <div class="form-row">
          <div class="form-label">Уровень предыдущего образования</div>
          <v-select
            v-model="educationData.prev_ed"
            :items="educationOptions"
            item-title="text"
            item-value="value"
            variant="outlined"
            density="compact"
            hide-details
            @update:modelValue="onFieldUpdate('prev_ed', $event)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Средний балл аттестата</div>
          <v-text-field
            v-model="educationData.average"
            variant="outlined"
            density="compact"
            hide-details
            type="number"
            step="0.01"
            @input="onFieldUpdate('average', $event.target.value)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Наименование образовательной организации</div>
          <v-text-field
            v-model="educationData.name"
            variant="outlined"
            density="compact"
            hide-details
            @input="onFieldUpdate('name', $event.target.value)"
          />
        </div>

        <div class="form-row">
          <fieldset>
            <legend>Адрес образовательной организации:</legend>
            <div class="form-row">
              <div class="form-label">Страна</div>
              <v-text-field
                v-model="educationData.country"
                variant="outlined"
                density="compact"
                hide-details
                @input="onFieldUpdate('country', $event.target.value)"
              />
            </div>
            <div class="form-row">
              <div class="form-label">Населенный пункт</div>
              <v-text-field
                v-model="educationData.city"
                variant="outlined"
                density="compact"
                hide-details
                @input="onFieldUpdate('city', $event.target.value)"
              />
            </div>
            <div class="form-row">
              <div class="form-label">Район</div>
              <v-text-field
                v-model="educationData.region"
                variant="outlined"
                density="compact"
                hide-details
                @input="onFieldUpdate('region', $event.target.value)"
              />
            </div>
          </fieldset>
        </div>

        <div class="form-row">
          <div class="form-label">Год окончания</div>
          <v-text-field
            v-model="educationData.year"
            type="number"
            placeholder="2022"
            variant="outlined"
            density="compact"
            hide-details
            @input="onFieldUpdate('year', $event.target.value)"
          />
          <div class="form-label">Дата получения</div>
          <v-text-field
            v-model="educationData.ed_date"
            type="date"
            variant="outlined"
            density="compact"
            hide-details
            @update:modelValue="onFieldUpdate('ed_date', $event)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">База образования</div>
          <v-radio-group
            v-model="educationData.ed_type"
            hide-details
            @update:modelValue="onFieldUpdate('ed_type', $event)"
          >
            <v-radio
              v-for="item in baseEducation"
              :key="item.key"
              :label="item.name"
              :value="item.key"
            />
          </v-radio-group>
        </div>

        <div class="form-row">
          <div class="form-label">Наименование документа</div>
          <v-autocomplete
            :items="documentsEducation"
            v-model="educationData.document_id"
            item-title="name"
            item-value="key"
            hide-details
            variant="outlined"
            density="compact"
            @update:modelValue="onFieldUpdate('document_id', $event)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Данные документа</div>
          <v-text-field
            v-model="educationData.series"
            variant="outlined"
            label="Серия"
            density="compact"
            hide-details
            @input="onFieldUpdate('series', $event.target.value)"
          />
          <v-text-field
            v-model="educationData.number"
            variant="outlined"
            label="№"
            density="compact"
            hide-details
            @input="onFieldUpdate('number', $event.target.value)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Изучаемые языки</div>
          <v-checkbox
            v-for="lang in languages"
            :key="lang.key"
            :label="lang.name"
            :value="lang.key"
            v-model="selectedLanguages"
            hide-details
            density="compact"
            @update:modelValue="onLanguagesUpdate"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Движение студента</div>
          <div v-if="orders.length === 0" class="text-caption text-grey">
            Нет данных о движении
          </div>
          <v-list v-else density="compact" class="orders-list">
            <v-list-item
              v-for="(order, i) in orders"
              :key="i"
              :href="`${$model.wwwroot}/blocks/manage_groups/documents/newDocument.php?id=${order.id}`"
              target="_blank"
            >
              <template #prepend>
                <v-icon color="primary"> mdi-file-document-outline</v-icon>
              </template>
              <v-list-item-title class="item-title">
                {{ order.name }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </div>

        <div class="form-row">
          <div class="form-label">Договоры</div>
          <div v-if="contracts.length === 0" class="text-caption text-grey">
            Нет данных о договорах
          </div>
          <v-list v-else density="compact" class="orders-list">
            <v-list-item
              v-for="(contract, i) in contracts"
              :key="i"
              :href="`${$model.wwwroot}/blocks/manage_groups/admission_new/print_contract.php?id=${contract.id}&student_id=${$route.params.personal_id}`"
              target="_blank"
            >
              <template #prepend>
                <v-icon color="primary">mdi-printer-outline</v-icon>
              </template>
              <v-list-item-title class="item-title">
                {{ contract.name }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </div>

        <div class="form-row">
          <div class="form-label">Данные диплома</div>
          <div
            v-if="Object.keys(diploms).length === 0"
            class="text-caption text-grey"
          >
            Нет данных о дипломах
          </div>
          <v-list v-else density="compact">
            <v-list-item v-for="(name, id) in diploms" :key="id">
              <template #prepend>
                <v-icon color="primary"> mdi-text-box-outline</v-icon>
              </template>
              <v-list-item-title class="item-title">
                {{ name }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>
    </template>
  </v-card>
</template>

<script>
export default {
  name: "EducationData",

  props: {
    student: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      hasError: false,
      errorMessage: null,

      baseEducation: [],
      documentsEducation: [],
      languages: [],
      selectedLanguages: [],
      orders: [],
      contracts: [],
      diploms: [],

      educationData: {
        prev_ed: 0,
        average: null,
        name: "",
        country: "",
        city: "",
        region: "",
        year: null,
        ed_date: null,
        ed_type: null,
        document_id: null,
        series: "",
        number: "",
      },

      educationOptions: [
        { value: 0, text: "-" },
        { value: 1, text: "НПО (училище)" },
        { value: 2, text: "СПО (техникум, колледж)" },
        { value: 3, text: "ВПО" },
      ],
    };
  },

  created() {
    this.loadData();
  },

  methods: {
    loadData() {
      this.isLoading = true;
      this.hasError = false;
      this.errorMessage = null;

      const promises = [
        this.loadDirectoryData(),
        this.getStudentEducation(),
        this.getStudentOrder(),
        this.getContracts(),
        this.getDiploms(),
      ];

      Promise.allSettled(promises)
        .then((results) => {
          const hasError = results.some((r) => r.status === "rejected");
          if (hasError) {
            this.hasError = true;
            this.errorMessage = "Ошибка загрузки данных образования";
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    loadDirectoryData() {
      return this.axios
        .post(
          `${this.$model.wwwroot}/procollege/core_procollege/helpers/DirectoryLoader/Load`,
          new URLSearchParams({
            directory: JSON.stringify([
              "baseEducation",
              "documentsEducation",
              "admLanguages",
            ]),
          })
        )
        .then((res) => {
          this.baseEducation = [];
          this.documentsEducation = [];
          this.languages = [];

          if (res.data && res.data.baseEducation) {
            for (let i in res.data.baseEducation) {
              this.baseEducation.push({
                key: res.data.baseEducation[i].type_ed,
                name: res.data.baseEducation[i].name,
              });
            }
          }

          if (res.data && res.data.documentsEducation) {
            for (let i in res.data.documentsEducation) {
              this.documentsEducation.push({
                key: res.data.documentsEducation[i].code,
                name: res.data.documentsEducation[i].name,
              });
            }
          }

          if (res.data && res.data.admLanguages) {
            for (let i in res.data.admLanguages) {
              this.languages.push({
                key: res.data.admLanguages[i].id,
                name: res.data.admLanguages[i].name,
              });
            }
          }
        })
        .catch((error) => {
          console.error("Ошибка загрузки справочников:", error);
          throw error;
        });
    },

    getStudentEducation() {
      if (!this.$route.params.personal_id) {
        return Promise.resolve();
      }

      return this.axios
        .post(
          `${this.$model.wwwroot}/procollege/manage_groups/secretariat/studentCard/getStudentEducation`,
          new URLSearchParams({
            personal_id: this.$route.params.personal_id,
          })
        )
        .then((res) => {
          if (res.data) {
            const { languages, ...restData } = res.data;
            this.educationData = { ...this.educationData, ...restData };

            this.selectedLanguages = [];
            if (languages && typeof languages === "object") {
              for (let key in languages) {
                const langItem = languages[key];
                if (langItem && langItem.language_id) {
                  this.selectedLanguages.push(String(langItem.language_id));
                }
              }
            }
          }
        })
        .catch((error) => {
          console.error("Ошибка загрузки данных об образовании:", error);
          throw error;
        });
    },

    getStudentOrder() {
      if (!this.$route.params.personal_id) {
        return Promise.resolve();
      }

      return this.axios
        .post(
          `${this.$model.wwwroot}/procollege/manage_groups/secretariat/studentCard/getStudentOrder`,
          new URLSearchParams({
            personal_id: this.$route.params.personal_id,
          })
        )
        .then((res) => {
          this.orders = [];
          if (res.data && Array.isArray(res.data)) {
            for (let i in res.data) {
              this.orders.push({
                id: res.data[i].id,
                name: res.data[i].name,
              });
            }
          }
        })
        .catch((error) => {
          console.error("Ошибка загрузки данных приказов:", error);
          throw error;
        });
    },

    getContracts() {
      if (!this.$route.params.personal_id) {
        return Promise.resolve();
      }

      return this.axios
        .post(
          `${this.$model.wwwroot}/procollege/manage_groups/secretariat/studentCard/getContracts`,
          new URLSearchParams({
            personal_id: this.$route.params.personal_id,
          })
        )
        .then((res) => {
          this.contracts = [];
          if (res.data && Array.isArray(res.data)) {
            for (let i in res.data) {
              this.contracts.push({
                id: res.data[i].id,
                name:
                  (res.data[i].name || "") +
                  " " +
                  (res.data[i].date || "") +
                  " № " +
                  (res.data[i].num || ""),
              });
            }
          }
        })
        .catch((error) => {
          console.error("Ошибка загрузки данных договоров:", error);
          throw error;
        });
    },

    getDiploms() {
      if (!this.$route.params.personal_id) {
        return Promise.resolve();
      }

      return this.axios
        .post(
          `${this.$model.wwwroot}/procollege/manage_groups/secretariat/studentCard/getDiploms`,
          new URLSearchParams({
            personal_id: this.$route.params.personal_id,
          })
        )
        .then((res) => {
          this.diploms = res.data;
        })
        .catch((error) => {
          console.error("Ошибка загрузки данных дипломов:", error);
          throw error;
        });
    },

    onFieldUpdate(field, value) {
      this.educationData[field] = value;
      this.emitUpdate();
    },

    onLanguagesUpdate() {
      this.emitUpdate();
    },

    emitUpdate() {
      // Отправляем данные образования в родительский компонент
      this.$emit("update:student", {
        ...this.student,
        education: this.educationData,
        languages: this.selectedLanguages,
      });
    },
  },
};
</script>

<style scoped>
.item-title {
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: break-word;
}

::v-deep(.v-checkbox .v-selection-control__input),
:deep(.v-radio .v-selection-control__input) {
  color: rgba(0, 75, 140, 1) !important;
}
</style>